/**
 * Pagina di gestione moduli per ramo operativo.
 *
 * Layout a due colonne:
 * - Sinistra: lista rami (UO) del tenant con badge conteggio moduli attivi
 * - Destra: checklist moduli disponibili con toggle on/off
 *
 * Visibile solo ad admin. Route: /admin/moduli
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

interface UnitaBrief {
  id: string;
  nome: string;
  tipo: string;
}

interface ModuloStato {
  modulo: string;
  attivo: boolean;
}

const GestioneModuli: React.FC = () => {
  const token = localStorage.getItem('token') ?? '';

  const [unita, setUnita] = useState<UnitaBrief[]>([]);
  const [selectedUO, setSelectedUO] = useState<string | null>(null);
  const [moduliDisponibili, setModuliDisponibili] = useState<string[]>([]);
  const [moduliUO, setModuliUO] = useState<ModuloStato[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Conta moduli attivi per ogni UO (cache locale)
  const [conteggioModuli, setConteggioModuli] = useState<Record<string, number>>({});

  const headers = { Authorization: `Bearer ${token}` };

  // Carica UO e moduli disponibili
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [unitaRes, moduliRes] = await Promise.all([
          fetch('/api/v1/unita', { headers }),
          fetch('/api/v1/unita/moduli-disponibili', { headers }),
        ]);
        if (unitaRes.ok) {
          const data: UnitaBrief[] = await unitaRes.json();
          setUnita(data);
          if (data.length > 0 && !selectedUO) {
            setSelectedUO(data[0].id);
          }

          // Carica conteggio moduli per ogni UO
          const conteggi: Record<string, number> = {};
          for (const u of data) {
            const res = await fetch(`/api/v1/unita/${u.id}/moduli`, { headers });
            if (res.ok) {
              const mods: ModuloStato[] = await res.json();
              conteggi[u.id] = mods.filter((m) => m.attivo).length;
            }
          }
          setConteggioModuli(conteggi);
        }
        if (moduliRes.ok) {
          const data = await moduliRes.json();
          setModuliDisponibili(data.moduli ?? []);
        }
      } catch {
        setError('Errore caricamento dati');
      } finally {
        setLoading(false);
      }
    };
    void init();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carica moduli della UO selezionata
  const caricaModuliUO = useCallback(async (uoId: string) => {
    try {
      const res = await fetch(`/api/v1/unita/${uoId}/moduli`, { headers });
      if (res.ok) {
        setModuliUO(await res.json());
      }
    } catch { /* ignore */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedUO) {
      void caricaModuliUO(selectedUO);
    }
  }, [selectedUO, caricaModuliUO]);

  // Toggle modulo e salva
  const toggleModulo = async (modulo: string) => {
    if (!selectedUO) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Calcola nuova lista moduli attivi
    const attivi = new Set(moduliUO.filter((m) => m.attivo).map((m) => m.modulo));
    if (attivi.has(modulo)) {
      attivi.delete(modulo);
    } else {
      attivi.add(modulo);
    }

    try {
      const res = await fetch(`/api/v1/unita/${selectedUO}/moduli`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduli: Array.from(attivi) }),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const updated: ModuloStato[] = await res.json();
      setModuliUO(updated);
      setConteggioModuli((prev) => ({
        ...prev,
        [selectedUO]: updated.filter((m) => m.attivo).length,
      }));
      setSuccess('Moduli aggiornati');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const moduliAttivi = new Set(moduliUO.filter((m) => m.attivo).map((m) => m.modulo));
  const selectedUOData = unita.find((u) => u.id === selectedUO);

  if (loading) {
    return <div className="p-6 text-muted-foreground">Caricamento...</div>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Gestione moduli</h1>
      <p className="text-sm text-muted-foreground">
        Attiva o disattiva i moduli disponibili per ogni ramo operativo.
      </p>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border border-green-500/50 bg-green-500/10 px-4 py-2 text-sm text-green-700">
          {success}
        </div>
      )}

      {unita.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nessun ramo operativo configurato. Crea almeno un'unita organizzativa nella pagina Azienda.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-[280px_1fr] gap-6">
          {/* Colonna sinistra: lista rami */}
          <Card>
            <CardHeader>
              <CardTitle>Rami operativi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 p-2">
              {unita.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setSelectedUO(u.id)}
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors"
                  style={{
                    backgroundColor: u.id === selectedUO ? 'var(--color-muted)' : 'transparent',
                    fontWeight: u.id === selectedUO ? 600 : 400,
                  }}
                >
                  <div>
                    <div>{u.nome}</div>
                    <div className="text-xs text-muted-foreground">{u.tipo}</div>
                  </div>
                  <Badge variant="secondary">
                    {conteggioModuli[u.id] ?? 0} moduli
                  </Badge>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Colonna destra: checklist moduli */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  Moduli {selectedUOData ? `— ${selectedUOData.nome}` : ''}
                </CardTitle>
                <Badge>
                  {moduliAttivi.size} / {moduliDisponibili.length} attivi
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {moduliDisponibili.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nessun modulo registrato nel sistema.
                </p>
              ) : (
                <div className="space-y-2">
                  {moduliDisponibili.map((modulo) => {
                    const isActive = moduliAttivi.has(modulo);
                    return (
                      <div
                        key={modulo}
                        className="flex items-center justify-between rounded-md border px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-2.5 w-2.5 rounded-full"
                            style={{
                              backgroundColor: isActive ? '#22c55e' : '#d1d5db',
                            }}
                          />
                          <span className="text-sm font-medium">{modulo}</span>
                        </div>
                        <Button
                          variant={isActive ? 'outline' : 'default'}
                          size="sm"
                          onClick={() => toggleModulo(modulo)}
                          disabled={saving}
                        >
                          {isActive ? 'Disattiva' : 'Attiva'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GestioneModuli;
